import { CustomSeriesRenderItem, EChartsOption, SeriesOption, graphic } from 'echarts';
import { addMinutes } from 'date-fns';
import { NotificationSeverity } from 'src/api';
import { TranslateService } from '@ngx-translate/core';
import { ChartColorService } from '../chart-color.service';
import { opModeToSIDMapping } from 'src/utils';
import { ChartConfig } from '../chart.service';

export interface MachineEvent {
  severity: NotificationSeverity;
  text: string;
}

export interface MachineEventMeasure {
  events: {
    data: MachineEvent[];
    timestamps: Date[];
  };
  operationModes: {
    data: number[];
    timestamps: Date[];
  };
}

export interface MachineEventChartContent {
  main: MachineEventMeasure;
  compare?: MachineEventMeasure;
}

export function getMachineEventChartOptions(
  data: MachineEventMeasure,
  translate: TranslateService,
  darkMode?: boolean,
  chartConfig?: ChartConfig
): EChartsOption[] {
  const config = chartConfig ? chartConfig : {};
  const dataOpModes: any = [];
  const dataEvents: any = [];
  const series: SeriesOption[] = [];

  const xAxisEvents = data.events.timestamps;
  const xAxisOpModes = data.operationModes.timestamps;

  const yAxisData = [translate.instant('MC_Operation State'), translate.instant('MC_Event')];

  const events = data.events.data.map((event) => {
    return {
      severity: event.severity,
      text: event.text,
      color: ChartColorService.eventToColorMapping[event.severity](darkMode),
    };
  });

  const operationModes = data.operationModes.data.map((mode) => {
    return {
      name: translate.instant(opModeToSIDMapping[mode]),
      color: ChartColorService.opModeToColorMapping[mode](darkMode),
    };
  });

  operationModes.forEach((mode, index) => {
    const startTime = xAxisOpModes[index];
    const endTime = xAxisOpModes[index + 1];

    dataOpModes.push({
      name: mode.name,
      value: [0, startTime, endTime],
      itemStyle: {
        normal: {
          color: mode.color,
        },
      },
    });
  });

  events.forEach((event, index) => {
    const baseTime = xAxisEvents[index];
    // every event lasts 10 minutes, so that its visible in the chart
    // if width of event should be bigger, add more minutes
    const duration = addMinutes(new Date(xAxisEvents[index]), 10);

    const severity = event.severity;

    (dataEvents[severity] = dataEvents[severity] || []).push({
      severity: severity,
      name: severity,
      text: event.text,
      value: [1, baseTime, duration],
      color: event.color,
    });
  });

  Object.keys(dataEvents).forEach((event) => {
    const color = dataEvents[event][0].color;
    const eventData = dataEvents[event];
    const name = translate.instant(`MC_Severity_${event}`);
    series.push({
      type: 'custom',
      name: name,
      renderItem: <CustomSeriesRenderItem>renderItem,
      itemStyle: {
        opacity: 0.8,
        color: color,
      },
      encode: {
        x: [1, 2],
        y: 0,
      },
      data: eventData,
    });
  });

  function renderItem(params: any, api: any) {
    const categoryIndex = api.value(0);
    const start = api.coord([api.value(1), categoryIndex]);
    const end = api.coord([api.value(2), categoryIndex]);
    // to increase height of event or condition mode bar increase factor
    const height = api.size([0, 1])[1] * 0.2;

    const rectShape = graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height / 2,
        width: end[0] - start[0],
        height: height,
      },
      {
        x: params.coordSys.x,
        y: params.coordSys.y,
        width: params.coordSys.width,
        height: params.coordSys.height,
      }
    );

    return (
      rectShape && {
        type: 'rect',
        shape: rectShape,
        style: api.style(),
      }
    );
  }

  return [
    {
      grid: {
        top: 0,
        left: 110,
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0],
          filterMode: 'none',
          start: config.zoomState ? config.zoomState.start : 0,
          end: config.zoomState ? config.zoomState.end : 100,
        },
      ],
      legend: {
        show: true,
        bottom: 0,
        icon: 'circle',
        selected: config.selectedState ? config.selectedState : undefined,
      },
      tooltip: {
        formatter: function (params: any) {
          return `${params.marker} ${params.name}${
            params.data.text ? ': ' + params.data.text : ''
          }`;
        },
        confine: true,
      },
      xAxis: {
        type: 'time',
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'category',
        axisTick: {
          show: false,
        },
        data: yAxisData,
      },
      series: [
        {
          type: 'custom',
          renderItem: <CustomSeriesRenderItem>renderItem,
          itemStyle: {
            opacity: 0.8,
          },
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: dataOpModes,
        },
        ...series,
      ],
    },
  ];
}
